import { bankCountries, mobileOperators } from '@/mocks/sendMoney';
import type { BankCountry, MobileOperator } from '@/types/portal';

/** Mobile money operators that settle in a given currency. */
export function operatorsForCurrency(currency: string): MobileOperator[] {
  return mobileOperators.filter((item) => item.currency === currency);
}

/** Look an operator up by its id. */
export function operatorById(id: string | null | undefined): MobileOperator | undefined {
  if (!id) return undefined;
  return mobileOperators.find((item) => item.id === id);
}

/** The digits of a dial code, e.g. "+254" -> "254". */
function dialCodeDigits(dialCode: string): string {
  return dialCode.replace(/\D/g, '');
}

/** The bank market a wallet's currency settles from, used as the default pick. */
export function defaultCountry(currency: string): BankCountry {
  return bankCountries.find((item) => item.currency === currency) ?? bankCountries[0];
}

/** Look a bank market up by ISO code. */
export function countryByCode(code: string): BankCountry | undefined {
  return bankCountries.find((item) => item.code === code);
}

/** Look a bank market up by dial code. */
export function countryByDialCode(dialCode: string): BankCountry | undefined {
  return bankCountries.find((item) => item.dialCode === dialCode);
}

/** Every bank market, sorted alphabetically for the country picker. */
function sortedCountries(): BankCountry[] {
  return [...bankCountries].sort((a, b) => a.name.localeCompare(b.name));
}

/** Filter bank markets by a country name or dial-code search. */
export function searchCountries(query: string): BankCountry[] {
  const text = query.trim().toLowerCase();
  if (!text) return sortedCountries();
  return sortedCountries().filter(
    (item) => item.name.toLowerCase().includes(text) || item.dialCode.includes(text),
  );
}

/** Filter a market's banks by a free-text search. */
export function searchBanks(country: BankCountry, query: string): string[] {
  const text = query.trim().toLowerCase();
  if (!text) return country.banks;
  return country.banks.filter((bank) => bank.toLowerCase().includes(text));
}

/** Stable 2–3 digit code shown beside a bank name, echoing national sort codes. */
export function bankCode(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 1000;
  }
  return String(hash).padStart(2, '0');
}

/** Strip a dial code off a stored number so it can be edited locally. */
export function stripDial(number: string, dialCode: string): string {
  const digits = dialCodeDigits(dialCode);
  return number.startsWith(digits) ? number.slice(digits.length) : number;
}

/** Local display form of a number, e.g. "079 940 2657". */
export function formatLocalNumber(number: string, dialCode: string): string {
  const rest = stripDial(number, dialCode);
  const local = rest.startsWith('0') ? rest : `0${rest}`;
  return local.replace(/(\d{3})(\d{3})(\d{3,})/, '$1 $2 $3');
}

/** Hide all but the last four digits of a bank account. */
export function maskAccount(account: string): string {
  return account.length > 4 ? `•••• ${account.slice(-4)}` : account;
}

/** Dial codes a mobile payment can originate from for a given currency. */
export function dialCodesForCurrency(currency: string): string[] {
  const codes = Array.from(
    new Set(bankCountries.filter((item) => item.currency === currency).map((item) => item.dialCode)),
  );
  return codes.length > 0 ? codes : ['+254', '+255', '+256'];
}

/** The best-match dial code for a stored international number, longest prefix first. */
export function dialCodeForNumber(number: string): string | undefined {
  const match = [...bankCountries]
    .sort(
      (a, b) => dialCodeDigits(b.dialCode).length - dialCodeDigits(a.dialCode).length,
    )
    .find((item) => number.startsWith(dialCodeDigits(item.dialCode)));
  return match?.dialCode;
}